import { useEffect } from 'react';

interface PageMeta {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  jsonLd?: Record<string, unknown>;
}

const BASE_URL = 'https://lottolab.app';

export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = meta.title;

    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    const createdElements: HTMLElement[] = [];

    setMetaTag('description', meta.description);
    setMetaTag('keywords', meta.keywords);

    if (meta.canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
        createdElements.push(link);
      }
      link.setAttribute('href', meta.canonical);
    }

    const ogTitle = meta.ogTitle || meta.title;
    const ogDesc = meta.ogDescription || meta.description;
    const ogUrl = meta.ogUrl || meta.canonical || BASE_URL;

    setMetaTag('og:title', ogTitle, true);
    setMetaTag('og:description', ogDesc, true);
    setMetaTag('og:url', ogUrl, true);
    setMetaTag('twitter:title', ogTitle);
    setMetaTag('twitter:description', ogDesc);

    let jsonLdScript: HTMLScriptElement | null = null;
    if (meta.jsonLd) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';
      jsonLdScript.textContent = JSON.stringify(meta.jsonLd);
      document.head.appendChild(jsonLdScript);
      createdElements.push(jsonLdScript);
    }

    return () => {
      document.title = previousTitle;
      createdElements.forEach((el) => el.remove());
    };
  }, [meta.title, meta.description, meta.keywords, meta.canonical, meta.ogTitle, meta.ogDescription, meta.ogUrl, meta.jsonLd]);
}